// ============================================
// Vendor Order List
// Filterable, searchable table of all orders
// ============================================

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Search,
  Loader,
  AlertCircle,
  ChevronRight,
  Download,
} from 'lucide-react';
import { getAllOrders } from '../../services/firestore/orderService';
import { exportOrdersCsv } from '../../services/vendor/vendorExport';
import type { Order, OrderStatus } from '../../types/order';

const STATUS_TABS: { label: string; value: OrderStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Paid', value: 'paid' },
  { label: 'Processing', value: 'processing' },
  { label: 'Shipped', value: 'shipped' },
  { label: 'Delivered', value: 'delivered' },
];

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-text-muted/20 text-text-muted',
  paid: 'bg-gold/15 text-gold',
  processing: 'bg-suboptimal/15 text-suboptimal',
  shipped: 'bg-optimal/15 text-optimal',
  delivered: 'bg-text-secondary/20 text-text-secondary',
  cancelled: 'bg-critical/15 text-critical',
};

export function VendorOrderList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  async function loadOrders() {
    try {
      setLoading(true);
      const data = await getAllOrders();
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }

  const filteredOrders = useMemo(() => {
    let result = orders;

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter((o) => o.status === statusFilter);
    }

    // Filter by search query (email or order ID)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          o.customerEmail.toLowerCase().includes(q)
      );
    }

    return result;
  }, [orders, statusFilter, searchQuery]);

  function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function formatCents(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader size={32} className="text-accent-light animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20 gap-3 text-critical">
        <AlertCircle size={24} />
        <p className="text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-display text-text-primary mb-1">Orders</h2>
          <p className="text-text-muted text-base">
            {orders.length} total {orders.length === 1 ? 'order' : 'orders'}
          </p>
        </div>
        <button
          onClick={() => {
            setExportError(null);
            try {
              exportOrdersCsv(filteredOrders);
            } catch (err) {
              setExportError(
                err instanceof Error ? err.message : 'CSV export failed'
              );
            }
          }}
          disabled={filteredOrders.length === 0}
          aria-label={
            filteredOrders.length === 0
              ? 'Export CSV — no orders to export'
              : `Export ${filteredOrders.length} orders as CSV`
          }
          title={
            filteredOrders.length === 0
              ? 'No orders to export'
              : `Export ${filteredOrders.length} orders`
          }
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-base font-medium bg-accent-light text-white hover:bg-accent-light/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={16} aria-hidden="true" />
          Export CSV
        </button>
      </div>

      {exportError && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-critical/10 text-critical text-base" role="alert">
          <AlertCircle size={16} />
          <span>{exportError}</span>
          <button
            onClick={() => setExportError(null)}
            className="ml-auto text-critical/60 hover:text-critical text-sm"
            aria-label="Dismiss export error"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by email or order ID..."
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-bg-card border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent-light/50 focus:border-accent-light transition-colors"
        />
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-base font-medium transition-colors ${
              statusFilter === tab.value
                ? 'bg-accent-light/15 text-accent-light'
                : 'bg-bg-card text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Orders table */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-bg-tertiary">
                <th className="text-left px-4 py-3 text-text-muted font-medium text-sm">Order ID</th>
                <th className="text-left px-4 py-3 text-text-muted font-medium text-sm">Customer</th>
                <th className="text-left px-4 py-3 text-text-muted font-medium text-sm">Email</th>
                <th className="text-left px-4 py-3 text-text-muted font-medium text-sm">Bundle</th>
                <th className="text-right px-4 py-3 text-text-muted font-medium text-sm">Total</th>
                <th className="text-center px-4 py-3 text-text-muted font-medium text-sm">Status</th>
                <th className="text-left px-4 py-3 text-text-muted font-medium text-sm">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr
                  key={order.id}
                  onClick={() => navigate(`/vendor/orders/${order.id}`)}
                  className="border-b border-bg-tertiary/50 hover:bg-bg-elevated/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-mono text-sm text-text-secondary">
                    {order.id.slice(0, 12)}...
                  </td>
                  <td className="px-4 py-3 text-text-primary">
                    {order.customerName || '--'}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {order.customerEmail}
                  </td>
                  <td className="px-4 py-3 text-text-secondary">
                    {order.bundleName || '--'}
                  </td>
                  <td className="px-4 py-3 text-right text-text-primary font-medium">
                    {formatCents(order.totalInCents)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-4 py-3 text-text-muted text-sm">
                    {formatDate(order.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <ChevronRight size={16} className="text-text-muted" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-bg-tertiary/50">
          {filteredOrders.map((order) => (
            <div
              key={order.id}
              onClick={() => navigate(`/vendor/orders/${order.id}`)}
              className="p-4 hover:bg-bg-elevated/50 cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-text-secondary">
                  {order.id.slice(0, 12)}...
                </span>
                <StatusBadge status={order.status} />
              </div>
              <p className="text-text-primary font-medium">
                {order.customerName || order.customerEmail}
              </p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-text-muted text-sm">{order.bundleName || '--'}</span>
                <span className="text-text-primary font-medium">
                  {formatCents(order.totalInCents)}
                </span>
              </div>
              <p className="text-text-muted text-sm mt-1">{formatDate(order.createdAt)}</p>
            </div>
          ))}
        </div>

        {filteredOrders.length === 0 && (
          <div className="py-12 text-center text-text-muted text-base">
            No orders found matching your filters.
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span
      className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
        STATUS_COLORS[status] || 'bg-bg-elevated text-text-secondary'
      }`}
    >
      {status}
    </span>
  );
}
